import { Typography } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ViewImagesDialog from "../../../components/dialogs/ImagesDialog";
import ViewNoteDialog from "../../../components/dialogs/ViewNoteDialog";
import DynamicTable from "../../../components/dynamicTable";
import AppHeader from "../../../components/header";
import useDebounce from "../../../components/hooks/useDebounce";
import AppLoader from "../../../components/loader";
import { AppContext } from "../../../lib/Context";
import {
  getAllLeadProposal,
  updateLeadProposal,
} from "../../../services/LeadProposalApi";
import { handleOpenSnackBar } from "../../../utils/helper";
import AddEditLeadProposalDialog from "./../../../components/dialogs/AddEditLeadProposalDialog";
import SearchTab from "./SearchTab";
import {
  setCurrentPage,
  setPageLimit,
  setTotalCount,
  setTotalPages,
} from "../../../lib/redux/slices/leadPaginationSlice";

const tableHeader = [
  "Created At",
  "Name",
  "Dates",
  "Note",
  "Lead Stage",
  "Current Provider",
  "Actions",
];

const displayRows = [
  "lead_created_date",
  "lead_detail",
  "lead_dates",
  "note",
  "lead_stage_status",
  "current_provider",
  "mark_asread",
];

const AdminProposalPage = () => {
  const openDialog = useRef(null);
  const openNoteDialog = useRef(null);
  const openImageDialog = useRef(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [allLeads, setAllLeads] = useState([]);
  const { setGeneralInfo, setLeadProposal } = useContext(AppContext);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const debounceSearchTerm = useDebounce(searchKeys?.proposalSearchValue, 1000);

  const paginatedData = useSelector(
    (state) => state.leadProposalPaginatedReducer,
  );

  useEffect(() => {
    fetchAllLeads(debounceSearchTerm, paginatedData.page, paginatedData.limit);
  }, [debounceSearchTerm, paginatedData.page, paginatedData.limit]);

  const fetchAllLeads = async (search, pageToLoad, limit) => {
    try {
      setIsLoading(true);
      const params = {
        search,
        page: pageToLoad,
        limit,
      };
      const response = await getAllLeadProposal(params);
      if (response?.data.status === "success") {
        let data = response?.data.data;
        let pagination = response?.data.pagination;
        setAllLeads(data);
        dispatch(setPageLimit(pagination.limit));
        dispatch(setTotalPages(pagination.totalPages));
        dispatch(setTotalCount(pagination.total));
        dispatch(setCurrentPage(pagination.page));
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setPageLimit(limit));
    dispatch(setCurrentPage(1));
  };

  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setCurrentPage(page));
  };

  const handleUpdateLeadStatus = async (row, index, value) => {
    if (value === "Not Converted") {
      Swal.fire({
        title: "Are you sure?",
        text: "Do you really want to mark this lead as not converted?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, mark as Not Converted!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            setIsLoading(true);
            const response = await updateLeadProposal(row?._id, {
              status: "Not Converted",
            });
            if (response?.data.status === "success") {
              fetchAllLeads(
                debounceSearchTerm,
                paginatedData.page,
                paginatedData.limit,
              );
              handleOpenSnackBar("success", response?.data.message);
            } else {
              handleOpenSnackBar("error", response?.data.message);
            }
          } catch (error) {
            handleOpenSnackBar("error", error?.message);
          } finally {
            setIsLoading(false);
          }
        }
      });
    }

    if (value === "Converted") {
      navigate("/add-lead");
      setLeadProposal({
        isConverted: true,
        data: row,
      });
      setGeneralInfo((prev) => ({
        ...prev,
        name: row.username,
        email: row?.email,
        businessName: row?.businessName || "",
        mobileNo: row?.contact || "",
        address1: row?.address || "",
      }));
    }
  };

  const handleOpenNoteDialog = (row) => {
    openNoteDialog.current.openDialog({
      data: row?.note,
    });
  };

  const handleEdit = (row) => {
    openDialog.current.openDialog({
      data: row,
      getData: () =>
        fetchAllLeads(
          debounceSearchTerm,
          paginatedData.page,
          paginatedData.limit,
        ),
      type: "edit",
    });
  };

  const handleOpenImagesDialog = (row) => {
    openImageDialog.current.openDialog(row?.images);
  };

  const handleUpdateLeadStageStatus = async (e, row) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to update the lead stage.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsLoading(true);
          const response = await updateLeadProposal(row._id, { leadStage: e });
          if (response?.data.status === "success") {
            fetchAllLeads(
              debounceSearchTerm,
              paginatedData.page,
              paginatedData.limit,
            );
            Swal.fire({
              title: "Updated!",
              text: "The lead stage has been updated.",
              icon: "success",
            });
          } else {
            handleOpenSnackBar("error", response?.data.message);
          }
        } catch (error) {
          handleOpenSnackBar("error", error?.message);
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  return (
    <AppHeader>
      <AppLoader isVisible={isLoading} />

      <SearchTab
        handleOpenDialog={() => {
          openDialog.current.openDialog({
            getData: () =>
              fetchAllLeads(
                debounceSearchTerm,
                paginatedData.page,
                paginatedData.limit,
              ),
            type: "add",
            data: null,
          });
        }}
      />

      <DynamicTable
        tableData={allLeads}
        displayRows={displayRows}
        tableHeader={tableHeader}
        handleUpdateLeadStatus={handleUpdateLeadStatus}
        handleNoteDialogdata={handleOpenNoteDialog}
        handleEditLeadProposal={handleEdit}
        handleOpenImagesDialog={handleOpenImagesDialog}
        handleUpdateLeadStageStatus={handleUpdateLeadStageStatus}
        isPaginated={true}
        paginatedData={paginatedData}
        onRowsPerPageChange={handleRowsPerPageChange}
        onPageChange={handlePageChange}
      />
      {allLeads?.length === 0 && isLoading === false && (
        <Typography variant="h5" fontWeight={600} textAlign={"center"}>
          No Data Found !
        </Typography>
      )}

      <ViewNoteDialog ref={openNoteDialog} />
      <AddEditLeadProposalDialog ref={openDialog} />
      <ViewImagesDialog ref={openImageDialog} />
    </AppHeader>
  );
};

export default AdminProposalPage;
